<template>
	<modal
		size="small-medium"
		:hide-footer="true"
		@close="close">
		<template slot="header">
			<span v-if="!data">{{ $t('Add User') }}</span>
			<span v-else>{{ $t('Edit User') }}</span>
		</template>
		<template slot="body">
			<form @submit.prevent="save">
				<!-- Role -->
				<div class="form-group roles">
					<label class="typo__label">Roles</label>
						<div class="mh-40px">
							<multiselect class="roles-input" v-model="role" tag-placeholder="Add Roles"
							placeholder="Search or add a roles" label="label" track-by="key"
							:options="roles" :multiple="true" :searchable="false" :taggable="true" @tag="addTag" :show-labels="false">
							<span class="arrow-multiselect" slot="caret">
								<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
							</span>
							</multiselect>
						</div>
					</div>
				<!-- Package Id -->
				<div class="form-group roles">
					<label class="typo__label">Package </label><label class="ml-1 text-small">(optional)</label>
					<div class="mh-40px">
						<multiselect class="roles-input" v-model="packageData" deselect-label="Can't remove this value" track-by="name" label="name"
							placeholder=" " :options="itemsPackages" :searchable="true" :allow-empty="false" :show-labels="false">
							<span class="arrow-multiselect" slot="caret">
								<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
							</span>
							<span slot="noResult">Oops! No package found.</span>
						</multiselect>
					</div>
				</div>
				<!-- Email -->
				<div class="form-group">
					<label>{{ $t('Email') }}</label>
					<input type="email" name="email" class="form-control" v-model="email" required>
					<span v-if="!isValidEmailAddress && email !== ''" class="text-danger text-small">{{$t('Invalid Email Address')}}</span>
				</div>
				<!-- Fullname -->
				<div class="form-group">
					<label>{{ $t('Fullname') }}</label>
					<input type="text" name="name" class="form-control" v-model="fullname" required @keydown="textKeydown($event)">
				</div>
				<!-- Password -->
				<div class="form-group mb-4" v-if="!data">
					<div class="d-flex justify-content-between">
						<label>{{ $t('Password') }}</label>
						<span class="font-small mt-1"
								:class="{'very-weak': warningError === 'Weak',
									'weak': warningError === 'Medium',
									'strong': warningError === 'Strong',
							}">
							{{warningError}}
						</span>
					</div>
					<input-password v-model="password" :required="false"/>
					<span class="text-danger text-small" v-if="warningError === 'Weak' && password">{{$t('Please enter a password of at least 8 characters, and use a combination of letters and numbers')}}</span>
				</div>
				<!-- Confirm Password -->
				<div class="form-group mb-4" v-if="!data">
					<div class="d-flex justify-content-between">
						<label>{{ $t('Confirm Password') }}</label>
						<label class="text-danger text-small" v-if="confirmPassword && confirmPassword !== password">{{$t('Password do not match')}}</label>
					</div>
					<input-password v-model="confirmPassword" :required="false" />
				</div>
				<!-- Phone -->
				<div class="form-group mb-4">
					<label>{{ $t('Phone') }}</label>
					<input type="number" name="phone" class="form-control" v-model="phone">
				</div>
				<!-- Is Verified -->
				<label class="custom-control custom-checkbox mb-3">
					<input type="checkbox" class="custom-control-input" v-model="isVerified">
					<span class="custom-control-label">{{ $t('Verified') }}</span>
				</label>
				<!-- Submit -->
				<button v-if="!data"
					type="submit"
					class="btn btn-submit btn-primary mt-3"
					:disabled="!isFormValid"
					:class="{ 'is-loading': isSaving }">
					{{ $t('Create User') }}
				</button>
				<button v-else
					type="submit"
					:disabled="!data"
					class="btn btn-submit btn-primary mt-3"
					:class="{ 'is-loading': isSaving }">
					{{ $t('Update User') }}
				</button>
			</form>
		</template>
	</modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Modal from '@/components/Modal.vue';
import InputPassword from '@/components/InputPassword.vue';
import { getAxiosErrorMessage, isValidEmail } from '@/lib/helper';
import user from '@/api/user';
import packagesApi from '@/api/packages';

export default {
	name: 'UserForm',
	props: {
		data: {
			type: Object,
		},
	},
	components: {
		Modal,
		InputPassword,
		Multiselect,
	},
	data() {
		return {
			email: '',
			password: '',
			confirmPassword: '',
			typePassword: 'password',
			fullname: '',
			phone: '',
			isVerified: false,
			lang: 'en',
			picture: '',
			role: [],
			roles: [
				{ key: 'client', label: 'Client' },
				{ key: 'super_admin', label: 'Super Admin' },
				{ key: 'collaborator', label: 'Collaborator' },
				{ key: 'finance', label: 'Finance' },
			],
			isSaving: false,
			warningError: '',
			score: '',
			itemsPackages: [],
			isFetchingPackage: false,
			packageData: null,
		};
	},
	methods: {
		addTag() {
			this.roles.push();
			this.role.push();
		},
		save() {
			this.isSaving = true;
			const params = this.params;
			const callback = (response) => {
				const user = response.data;
				const message = response.message;
				this.$emit('saved', user);
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Add User',
					text: message,
				});
				this.isSaving = false;
				this.close();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Add User',
					text: message,
				});
				this.isSaving = false;
			};
			if (this.data) {
				user.update(this.data.id, params, callback, errorCallback);
			} else {
				user.create(params, callback, errorCallback);
			}
		},
		close() {
			if (this.data) {
				const roles = this.role.map(a => a.key);
				this.data.roles = roles;
			}
			this.$emit('close');
		},
		initData() {
			this.reset();
			if (this.data) {
				this.setData(this.data);
			}
		},
		reset() {
			this.email = '';
			this.fullname = '';
			this.phone = '';
			this.isVerified = false;
			this.role = [];
			this.picture = '';
			this.lang = 'en';
			this.packageData = null;
		},
		setData(data) {
			for (let index = 0; index < data.roles.length; index++) {
				const role = data.roles[index];
				data.roles[index] = this.searchRoles(role, this.roles); // eslint-disable-line no-param-reassign
			}
			this.email = data.email;
			this.fullname = data.name;
			this.phone = data.phone;
			this.isVerified = data.is_verified;
			this.role = data.roles;
			this.picture = data.picture;
			this.lang = data.language;
			this.packageData = data.package_id;
		},
		showPassword() {
			if (this.typePassword === 'password') {
				this.typePassword = 'text';
			} else {
				this.typePassword = 'password';
			}
		},
		searchRoles(nameKey, roles) {
			for (let i = 0; i < roles.length; i++) {
				if (roles[i].key === nameKey) {
					return roles[i];
				}
			}
			return roles;
		},
		fetchPackage() {
			this.isFetchingPackage = true;
			const params = {
				page: 1,
				limit: 99999,
			};
			const callback = (response) => {
				const items = response.data;
				this.itemsPackages = items;
				if (this.data && this.data.userSubscriptions.length > 0) {
					const index = this.itemsPackages.findIndex(({ id }) => id === this.data.userSubscriptions[0].package_id);
					this.packageData = this.itemsPackages[index];
				}
				this.isFetchingPackage = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Packages',
					text: message,
				});
				this.isFetchingPackage = false;
			};
			packagesApi.getList(params, callback, errorCallback);
		},
		textKeydown(e) {
			if (/[^A-Z0-9 ]/gi.test(e.key)) {
				e.preventDefault();
			}
		},
	},
	computed: {
		params() {
			const roles = this.role.map(a => a.key);
			const roleResult = JSON.stringify(roles);
			const email = this.email.trim();
			const fullname = this.fullname.trim();
			const password = this.password;
			const params = {
				email,
				name: fullname,
				is_verified: this.isVerified,
				language: this.lang,
				picture: this.picture,
				roles: roleResult,
			};
			if (this.packageData) params.package_id = JSON.stringify(this.packageData.id);
			if (this.phone) {
				params.phone = this.phone.trim();
			}
			if (password && password !== '') {
				params.password = this.password;
			}
			return params;
		},
		isFormValid() {
			return (
				this.password === this.confirmPassword
				&& this.warningError !== 'Weak'
				&& this.isValidEmailAddress
				&& this.fullname !== ''
			);
		},
		isValidEmailAddress() {
			return isValidEmail(this.email);
		},
	},
	created() {
		this.initData();
		this.fetchPackage();
	},
	watch: {
		password() {
			if (this.password !== '') {
				/* eslint-disable global-require */
				const passwordStrength = require('check-password-strength');
				if (this.password.length < 8) {
					this.warningError = 'Weak';
				} else {
					/* eslint-enable global-require */
					this.warningError = passwordStrength(this.password).value;
				}
			}
			if (this.password === '') this.warningError = '';
		},
	},
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
